import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

/* ─── Animations ─── */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 201, 177, 0.08); }
  50% { box-shadow: 0 0 35px rgba(0, 201, 177, 0.2); }
`;

const countUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════ */

export const HeroSection = styled.section<{ $bgImage?: string }>`
  min-height: 100vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.15) 0%,
      rgba(10, 22, 40, 0.4) 40%,
      rgba(10, 22, 40, 0.92) 85%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 30%;
  background-attachment: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    background-attachment: scroll;
    background-position: center 20%;
  }
`;

export const HeroContent = styled.div`
  max-width: 900px;
  padding: 0 2rem;
  z-index: 2;
  animation: ${fadeInUp} 1s ease-out;
`;

export const HeroTag = styled.div`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.5rem 1.4rem;
  border-radius: 100px;
  margin-bottom: 1.5rem;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 800;
  line-height: 1.05;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

  span {
    background: var(--gradient-accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  color: var(--text-primary);
  margin-bottom: 2.5rem;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  background: rgba(10, 22, 40, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-lg);
`;

export const HeroButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 0.9rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;

export const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--text-primary);
  border: 2px solid rgba(255, 255, 255, 0.25);
  padding: 0.85rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);

  &:hover {
    border-color: var(--accent-teal);
    color: var(--accent-teal);
    background: rgba(0, 201, 177, 0.06);
  }
`;

/* ═══════════════════════════════════════════
   STATS BAR
   ═══════════════════════════════════════════ */

export const StatsBar = styled.section`
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 2.5rem 0;
`;

export const StatsContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

export const StatItem = styled.div`
  animation: ${countUp} 0.5s ease-out;
`;

export const StatNumber = styled.div`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  background: var(--gradient-accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: 0.3rem;
`;

/* ═══════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════ */

export const SectionEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2.2rem, 4.5vw, 3.2rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-accent);
    border-radius: 2px;
  }

  &[style*="center"] {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

export const AboutText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

/* ═══════════════════════════════════════════
   ABOUT
   ═══════════════════════════════════════════ */

export const AboutSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-primary);
`;

export const AboutContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 3rem;
  padding: 0 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const AboutContent = styled.div``;

export const AboutImage = styled.div`
  position: relative;

  img {
    border-radius: var(--border-radius-lg);
    width: 100%;
    box-shadow: var(--shadow-lg);
    transition: transform var(--transition-slow);
  }

  &:hover img { transform: scale(1.02); }

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: 12px;
    right: -12px;
    bottom: -12px;
    border: 2px solid var(--accent-teal);
    border-radius: var(--border-radius-lg);
    opacity: 0.15;
    z-index: -1;
  }
`;

export const SpeciesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

export const SpeciesTag = styled.span`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(0, 201, 177, 0.08);
  border: 1px solid rgba(0, 201, 177, 0.2);
  padding: 0.35rem 0.9rem;
  border-radius: 100px;
`;

/* ═══════════════════════════════════════════
   WHY FUSSAKI
   ═══════════════════════════════════════════ */

export const WhySection = styled.section`
  padding: 6rem 0;
  background: var(--bg-secondary);
  max-width: 100%;

  ${SectionTitle} {
    max-width: var(--container-max);
    margin-left: auto;
    margin-right: auto;
    padding: 0 1.5rem;
  }

  ${SectionEyebrow} {
    max-width: var(--container-max);
    margin-left: auto;
    margin-right: auto;
    padding: 0 1.5rem;
  }
`;

export const WhyGrid = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const WhyCard = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  transition: all var(--transition-base);
  position: relative;
  text-align: center;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-glow-teal);
  }
`;

export const WhyIcon = styled.div`
  color: var(--accent-gold);
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
`;

export const WhyTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.6rem;
`;

export const WhyText = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  line-height: 1.7;
  margin: 0;
`;

/* ═══════════════════════════════════════════
   CHARTERS / SERVICES
   ═══════════════════════════════════════════ */

export const ServicesSection = styled.section<{ $bgImage?: string }>`
  padding: 6rem 0;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.7) 0%,
      rgba(8, 18, 34, 0.6) 50%,
      rgba(10, 22, 40, 0.75) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center;
`;

export const ServicesContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const ServiceCard = styled.div`
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  transition: all var(--transition-base);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--gradient-gold);
    transform: scaleX(0);
    transition: transform var(--transition-base);
  }

  &:hover {
    background: rgba(10, 22, 40, 0.82);
    transform: translateY(-6px);
    border-color: rgba(244, 170, 0, 0.3);
    box-shadow: var(--shadow-glow-gold);

    &::before { transform: scaleX(1); }
  }
`;

export const ServiceIcon = styled.div`
  color: var(--accent-teal);
  margin-bottom: 1.2rem;
  animation: ${float} 3s ease-in-out infinite;
  animation-play-state: paused;

  ${ServiceCard}:hover & { animation-play-state: running; }
`;

export const ServiceTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.6rem;
`;

export const ServiceText = styled.p`
  color: var(--text-muted);
  font-size: 0.92rem;
  line-height: 1.7;
  flex: 1;
`;

export const ServicePrice = styled.div`
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--accent-gold);
  margin-top: 1rem;
  padding-top: 0.8rem;
  border-top: 1px solid var(--border-color);
`;

/* ═══════════════════════════════════════════
   CAPTAIN
   ═══════════════════════════════════════════ */

export const CaptainSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-secondary);
`;

export const CaptainWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CaptainCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2.5rem;
  align-items: center;
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

export const CaptainImage = styled.div`
  img {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid var(--accent-teal);
    box-shadow: var(--shadow-glow-teal);
  }

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
  }
`;

export const CaptainInfo = styled.div``;

export const CaptainName = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

export const CaptainRole = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--accent-teal);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
`;

export const CaptainQuote = styled.blockquote`
  font-style: italic;
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin: 0 0 1rem;
  padding-left: 1rem;
  border-left: 3px solid var(--accent-teal);

  @media (max-width: 768px) {
    border-left: none;
    padding-left: 0;
    border-top: 2px solid var(--accent-teal);
    padding-top: 1rem;
  }
`;

export const CaptainBadge = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.6rem;

  svg { color: var(--accent-gold); }

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

/* ═══════════════════════════════════════════
   CTA
   ═══════════════════════════════════════════ */

export const CtaSection = styled.section<{ $bgImage?: string }>`
  padding: 6rem 0;
  background-image:
    linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(15, 31, 53, 0.82) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 15%;
  background-attachment: fixed;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    background-attachment: scroll;
    background-position: center 25%;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    opacity: 0.25;
  }
  &::before { top: 0; }
  &::after { bottom: 0; }
`;

export const CtaContainer = styled.div`
  max-width: 750px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CtaTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

export const CtaSubtext = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 1rem 2.8rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;

/* ═══════════════════════════════════════════
   TESTIMONIALS
   ═══════════════════════════════════════════ */

export const TestimonialsSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-primary);
`;

export const TestimonialsContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const TestimonialsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const TestimonialCard = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  transition: all var(--transition-base);
  animation: ${pulseGlow} 5s ease-in-out infinite;
  animation-play-state: paused;

  &::before {
    content: '"';
    position: absolute;
    top: 0.8rem;
    right: 1.5rem;
    font-size: 4rem;
    font-family: Georgia, serif;
    color: var(--accent-teal);
    opacity: 0.1;
    line-height: 1;
    position: relative;
    float: right;
  }

  &:hover {
    border-color: var(--border-hover);
    transform: translateY(-4px);
    animation-play-state: running;
  }
`;

export const TestimonialRating = styled.div`
  display: flex;
  gap: 0.2rem;
  margin-bottom: 0.8rem;
  color: var(--accent-gold);
`;

export const TestimonialText = styled.p`
  font-style: italic;
  font-size: 0.95rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.2rem;
  clear: both;
`;

export const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
`;

export const TestimonialAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  border: 2px solid var(--accent-teal);
  flex-shrink: 0;

  img { width: 100%; height: 100%; object-fit: cover; }
`;

export const TestimonialInfo = styled.div``;

export const TestimonialName = styled.div`
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

export const TestimonialRole = styled.div`
  color: var(--text-muted);
  font-size: 0.8rem;
`;
